import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../api/admin/adminApi';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import '../../styles/AdminDashboard.css';
import styles from './AdminStaffPage.module.css';

/** Copied from config/staffPermissions.js — do not import server config. */
const STAFF_PRESETS = {
  operations: {
    label: 'Operations',
    permissions: ['orders.read', 'orders.manage', 'cards.read', 'cards.manage', 'fulfilment.manage', 'companies.read', 'audit.read'],
  },
  support: {
    label: 'Support',
    permissions: ['users.read', 'users.support', 'companies.read', 'cards.read', 'orders.read', 'audit.read'],
  },
  sales: {
    label: 'Sales',
    permissions: ['companies.read', 'companies.manage', 'users.read', 'promotions.read', 'promotions.manage', 'entitlements.read', 'orders.read'],
  },
  finance: {
    label: 'Finance',
    permissions: ['billing.read', 'billing.manage', 'orders.read', 'entitlements.read', 'promotions.read', 'audit.read'],
  },
};

const PERMISSION_GROUPS = [
  {
    key: 'orders',
    label: 'Orders',
    permissions: [
      { id: 'orders.read', label: 'View orders', risk: 'low' },
      { id: 'orders.manage', label: 'Manage orders and proofs', risk: 'medium' },
      { id: 'fulfilment.manage', label: 'Programme and fulfil cards', risk: 'medium' },
    ],
  },
  {
    key: 'cards',
    label: 'Cards',
    permissions: [
      { id: 'cards.read', label: 'View cards', risk: 'low' },
      { id: 'cards.manage', label: 'Manage cards and campaigns', risk: 'medium' },
    ],
  },
  {
    key: 'companies',
    label: 'Companies',
    permissions: [
      { id: 'companies.read', label: 'View companies', risk: 'low' },
      { id: 'companies.manage', label: 'Manage companies', risk: 'high' },
      { id: 'companies.delete', label: 'Delete companies', risk: 'critical' },
    ],
  },
  {
    key: 'users',
    label: 'Users',
    permissions: [
      { id: 'users.read', label: 'View users', risk: 'low' },
      { id: 'users.support', label: 'Support users (password reset, notes)', risk: 'medium' },
      { id: 'entitlements.read', label: 'View entitlements', risk: 'medium' },
      { id: 'entitlements.manage', label: 'Grant or change entitlements', risk: 'critical' },
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    permissions: [
      { id: 'billing.read', label: 'View billing', risk: 'medium' },
      { id: 'billing.manage', label: 'Change billing', risk: 'critical' },
    ],
  },
  {
    key: 'promotions',
    label: 'Promotions',
    permissions: [
      { id: 'promotions.read', label: 'View promotions', risk: 'low' },
      { id: 'promotions.manage', label: 'Grant promotional capacity', risk: 'high' },
    ],
  },
  {
    key: 'staff',
    label: 'Staff',
    permissions: [
      { id: 'staff.read', label: 'View staff access', risk: 'high' },
      { id: 'staff.manage', label: 'Grant staff access', risk: 'critical' },
    ],
  },
  {
    key: 'audit',
    label: 'Audit',
    permissions: [
      { id: 'audit.read', label: 'View audit log', risk: 'medium' },
    ],
  },
];

function idOf(value) {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
}

function personLabel(value) {
  if (!value) return '—';
  if (typeof value === 'object') return value.name || value.email || idOf(value) || '—';
  return String(value);
}

function personEmail(value) {
  if (!value || typeof value !== 'object') return '';
  return value.email || '';
}

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function statusPillClass(status) {
  if (status === 'active') return 'status-pill status-nfc-activated';
  if (status === 'suspended') return 'status-pill status-nfc-mismatch';
  return 'status-pill status-nfc-not-assigned';
}

function canManageStaff(account) {
  return (
    account?.role === 'admin'
    || Boolean(account?.staff?.superAdmin)
    || (account?.staff?.status === 'active' && account?.staff?.permissions?.includes('staff.manage'))
  );
}

function presetLabel(key) {
  if (!key || key === 'custom') return 'Custom';
  return STAFF_PRESETS[key]?.label || key;
}

function riskClass(risk) {
  if (risk === 'critical') return styles.riskCritical;
  if (risk === 'high') return styles.risk;
  return '';
}

function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel, busy }) {
  return (
    <div className="admin-drawer-overlay" onClick={onCancel} role="presentation">
      <div
        className={styles.confirm}
        role="dialog"
        aria-modal="true"
        aria-labelledby="staff-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="staff-confirm-title">{title}</h3>
        <p>{body}</p>
        <div className={styles.confirmActions}>
          <button type="button" className={styles.ghostBtn} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={styles.dangerBtn} onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function StaffForm({
  form,
  setForm,
  userHits,
  userQuery,
  setUserQuery,
  searchingUsers,
  onSearchUsers,
  onPickUser,
  onPresetChange,
  onTogglePermission,
  onSubmit,
  onCancel,
  saving,
  isEdit,
}) {
  return (
    <form className={styles.drawerForm} onSubmit={onSubmit}>
      {!isEdit ? (
        <label className={styles.field}>
          Person
          <input
            value={userQuery}
            onChange={(e) => {
              setUserQuery(e.target.value);
              onSearchUsers(e.target.value);
            }}
            placeholder="Search by name or email"
            autoComplete="off"
          />
          {searchingUsers ? <span>Searching…</span> : null}
          {userHits.length > 0 ? (
            <ul className={styles.hitList}>
              {userHits.map((u) => (
                <li key={u._id}>
                  <button
                    type="button"
                    aria-pressed={form.userId === u._id}
                    onClick={() => onPickUser(u)}
                  >
                    {u.name || 'Unnamed'} — {u.email}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {form.userId ? (
            <div className={styles.selected}>Selected: {form.userLabel || form.userId}</div>
          ) : null}
        </label>
      ) : (
        <div className={styles.selected}>Editing: {form.userLabel || form.userId}</div>
      )}

      <label className={styles.field}>
        Preset
        <select value={form.presetRole} onChange={(e) => onPresetChange(e.target.value)}>
          <option value="operations">Operations</option>
          <option value="support">Support</option>
          <option value="sales">Sales</option>
          <option value="finance">Finance</option>
          <option value="custom">Custom</option>
        </select>
      </label>

      <div className={styles.groups}>
        {PERMISSION_GROUPS.map((group) => (
          <details key={group.key} className={styles.group} open={group.key === 'orders' || group.key === 'staff'}>
            <summary>{group.label}</summary>
            <div className={styles.groupBody}>
              {group.permissions.map((perm) => (
                <label key={perm.id} className={styles.perm}>
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(perm.id)}
                    onChange={() => onTogglePermission(perm.id)}
                  />
                  <span>
                    {perm.label}
                    {(perm.risk === 'high' || perm.risk === 'critical') ? (
                      <span className={`${styles.permMeta} ${riskClass(perm.risk)}`}>
                        {perm.risk === 'critical' ? 'Critical risk' : 'High risk'}
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          </details>
        ))}
      </div>

      <details className={styles.tech}>
        <summary>Technical details</summary>
        <div className={styles.techBody}>
          {form.permissions.length
            ? form.permissions.join(', ')
            : 'No permission ids selected.'}
        </div>
      </details>

      <label className={styles.field}>
        Notes
        <textarea
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          placeholder="Why this access exists"
          rows={3}
        />
      </label>

      <div className={styles.rowActions}>
        <button type="button" className={styles.ghostBtn} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className={styles.primaryBtn} disabled={saving || !form.userId}>
          {saving ? 'Saving…' : (isEdit ? 'Save' : 'Grant')}
        </button>
      </div>
    </form>
  );
}

export default function AdminStaffPage() {
  const { account, registerShellMeta, setRefreshHandler } = useOutletContext() || {};
  const isDesktop = useMediaQuery('(min-width: 769px)');
  const manage = canManageStaff(account);

  const [staff, setStaff] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [query, setQuery] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    userId: '',
    userLabel: '',
    presetRole: 'support',
    permissions: [...STAFF_PRESETS.support.permissions],
    notes: '',
  });

  const [userQuery, setUserQuery] = useState('');
  const [userHits, setUserHits] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listStaff();
      setStaff(Array.isArray(data?.staff) ? data.staff : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load staff');
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    registerShellMeta?.({
      title: 'Staff',
      subtitle: 'Directory of people with Velox platform access.',
    });
  }, [registerShellMeta]);

  useEffect(() => {
    setRefreshHandler?.(() => load);
    return () => setRefreshHandler?.(null);
  }, [load, setRefreshHandler]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter((row) => {
      const name = personLabel(row.userId).toLowerCase();
      const email = personEmail(row.userId).toLowerCase();
      const preset = presetLabel(row.presetRole).toLowerCase();
      const status = String(row.status || '').toLowerCase();
      return name.includes(q) || email.includes(q) || preset.includes(q) || status.includes(q);
    });
  }, [staff, query]);

  const openCreate = () => {
    setEditingId('');
    setForm({
      userId: '',
      userLabel: '',
      presetRole: 'support',
      permissions: [...STAFF_PRESETS.support.permissions],
      notes: '',
    });
    setUserQuery('');
    setUserHits([]);
    setDrawerOpen(true);
    setNotice('');
    setError('');
  };

  const openEdit = (row) => {
    const uid = idOf(row.userId);
    setEditingId(uid);
    setForm({
      userId: uid,
      userLabel: `${personLabel(row.userId)}${personEmail(row.userId) ? ` — ${personEmail(row.userId)}` : ''}`,
      presetRole: row.presetRole || 'custom',
      permissions: Array.isArray(row.permissions) ? [...row.permissions] : [],
      notes: row.notes || '',
    });
    setUserQuery('');
    setUserHits([]);
    setDrawerOpen(true);
    setNotice('');
    setError('');
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
  };

  const onPresetChange = (presetRole) => {
    const preset = STAFF_PRESETS[presetRole];
    setForm((prev) => ({
      ...prev,
      presetRole,
      permissions: preset ? [...preset.permissions] : prev.permissions,
    }));
  };

  const onTogglePermission = (permId) => {
    setForm((prev) => {
      const has = prev.permissions.includes(permId);
      const permissions = has
        ? prev.permissions.filter((id) => id !== permId)
        : [...prev.permissions, permId];
      return { ...prev, permissions, presetRole: 'custom' };
    });
  };

  const onSearchUsers = async (value) => {
    const q = String(value || '').trim();
    if (q.length < 2) {
      setUserHits([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const data = await adminApi.listUsers({ q, limit: 8 });
      setUserHits(Array.isArray(data?.rows) ? data.rows : []);
    } catch {
      setUserHits([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const onPickUser = (user) => {
    setForm((prev) => ({
      ...prev,
      userId: user._id,
      userLabel: `${user.name || 'Unnamed'} — ${user.email}`,
    }));
    setUserQuery(user.email || '');
    setUserHits([]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!manage || !form.userId) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await adminApi.grantStaff({
        userId: form.userId,
        presetRole: form.presetRole,
        permissions: form.permissions,
        notes: form.notes,
      });
      setNotice(editingId ? 'Staff access updated.' : 'Staff access granted.');
      setDrawerOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save staff access');
    } finally {
      setSaving(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { action, row } = confirm;
    const userId = idOf(row.userId);
    setBusyId(userId);
    setError('');
    setNotice('');
    try {
      if (action === 'suspend') {
        await adminApi.suspendStaff(userId);
        setNotice('Staff access suspended.');
      } else {
        await adminApi.revokeStaff(userId);
        setNotice('Staff access revoked.');
      }
      setConfirm(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Action failed');
    } finally {
      setBusyId('');
    }
  };

  const renderActions = (row) => {
    const userId = idOf(row.userId);
    const disabled = busyId === userId || row.status === 'revoked' || !manage;
    return (
      <div className={styles.rowActions}>
        {manage && row.status !== 'revoked' ? (
          <button type="button" onClick={() => openEdit(row)} disabled={busyId === userId}>
            Edit
          </button>
        ) : null}
        <button
          type="button"
          disabled={disabled || row.status === 'suspended'}
          onClick={() => setConfirm({
            action: 'suspend',
            row,
            title: 'Suspend staff access?',
            body: `${personLabel(row.userId)} will immediately lose platform staff permissions until access is restored.`,
            confirmLabel: 'Suspend',
          })}
        >
          Suspend
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setConfirm({
            action: 'revoke',
            row,
            title: 'Revoke staff access?',
            body: `${personLabel(row.userId)} will permanently lose this staff grant. They can be granted again later if needed.`,
            confirmLabel: 'Revoke',
          })}
        >
          Revoke
        </button>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerCopy}>
          <h2>Staff</h2>
          <p>
            Manage who can operate Velox internals. Grants never set User.role to admin —
            they create StaffAccess records only.
          </p>
        </div>
        {manage && !loading && staff.length > 0 ? (
          <button type="button" className={styles.addBtn} onClick={openCreate}>
            Add staff
          </button>
        ) : null}
      </div>

      {error ? <div className="error" role="alert">{error}</div> : null}
      {notice ? <div className="success" role="status">{notice}</div> : null}

      {loading ? <p>Loading staff…</p> : null}

      {!loading && staff.length === 0 ? (
        <div className={styles.empty}>
          <h3>No staff yet</h3>
          <p>Grant platform access to a Velox account when someone needs operational tools.</p>
          {manage ? (
            <button type="button" className={styles.addBtn} onClick={openCreate}>
              Add staff
            </button>
          ) : null}
        </div>
      ) : null}

      {!loading && staff.length > 0 ? (
        <>
          <div className={styles.toolbar}>
            <input
              className={styles.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search staff"
              aria-label="Search staff"
            />
          </div>

          {isDesktop ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Preset</th>
                    <th>Status</th>
                    <th>Last change</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row._id || idOf(row.userId)}>
                      <td>
                        <span className={styles.personName}>{personLabel(row.userId)}</span>
                        {personEmail(row.userId) ? (
                          <span className={styles.personEmail}>{personEmail(row.userId)}</span>
                        ) : null}
                      </td>
                      <td><span className={styles.preset}>{presetLabel(row.presetRole)}</span></td>
                      <td><span className={statusPillClass(row.status)}>{row.status || '—'}</span></td>
                      <td>{formatWhen(row.updatedAt || row.createdAt)}</td>
                      <td>{renderActions(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.cards}>
              {filtered.map((row) => (
                <article key={row._id || idOf(row.userId)} className={styles.card}>
                  <div>
                    <div className={styles.personName}>{personLabel(row.userId)}</div>
                    {personEmail(row.userId) ? (
                      <span className={styles.personEmail}>{personEmail(row.userId)}</span>
                    ) : null}
                  </div>
                  <div className={styles.cardMeta}>
                    <span className={styles.preset}>{presetLabel(row.presetRole)}</span>
                    <span className={statusPillClass(row.status)}>{row.status || '—'}</span>
                    <span>{formatWhen(row.updatedAt || row.createdAt)}</span>
                  </div>
                  {renderActions(row)}
                </article>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <p className={styles.personEmail}>No staff match this search.</p>
          ) : null}
        </>
      ) : null}

      {drawerOpen ? (
        <div className="admin-drawer-overlay" onClick={closeDrawer}>
          <div
            className="admin-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={editingId ? 'Edit staff' : 'Add staff'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-drawer-header">
              <h3>{editingId ? 'Edit staff' : 'Add staff'}</h3>
              <button type="button" className="admin-drawer-close" onClick={closeDrawer} aria-label="Close">
                ×
              </button>
            </div>
            <div className="admin-drawer-body">
              <StaffForm
                form={form}
                setForm={setForm}
                userHits={userHits}
                userQuery={userQuery}
                setUserQuery={setUserQuery}
                searchingUsers={searchingUsers}
                onSearchUsers={onSearchUsers}
                onPickUser={onPickUser}
                onPresetChange={onPresetChange}
                onTogglePermission={onTogglePermission}
                onSubmit={onSubmit}
                onCancel={closeDrawer}
                saving={saving}
                isEdit={Boolean(editingId)}
              />
            </div>
          </div>
        </div>
      ) : null}

      {confirm ? (
        <ConfirmDialog
          title={confirm.title}
          body={confirm.body}
          confirmLabel={confirm.confirmLabel}
          onConfirm={runConfirm}
          onCancel={() => (busyId ? null : setConfirm(null))}
          busy={Boolean(busyId)}
        />
      ) : null}
    </div>
  );
}
